/**
 * HTS Reasoning Service
 *
 * Manages the SSE connection to the iterative HTS classification agent's
 * reasoning stream. Provides real-time chain-of-thought events to the UI
 * and handles the ask_user question/answer flow.
 */

import { API_ENDPOINTS } from 'config/api.config';
import apiClient from 'services/apiClient';

export interface ReasoningEvent {
  id: string;
  type: 'thinking' | 'text' | 'tool_call' | 'tool_result' | 'ask_user' | 'user_response' | 'phase_change' | 'classification_complete' | 'error' | 'stream_end'
    | 'tariff_lookup_step' | 'tariff_calculation_step' | 'tariff_flag_raised' | 'tariff_determination_complete' | 'tariff_narration';
  iteration: number;
  phase: string;
  timestamp: number;
  data: Record<string, any>;
}

export interface AgentQuestion {
  question: string;
  options?: string[];
  context: string;
}

export interface QuestionsResponse {
  status: string;
  questions: AgentQuestion[];
}

class ReasoningService {
  private controller: AbortController | null = null;

  /**
   * Connect to the reasoning SSE stream for a job.
   * Calls onEvent for each reasoning event received.
   * Returns when the stream ends or errors.
   */
  async watchReasoning(
    jobId: string,
    onEvent: (event: ReasoningEvent) => void,
  ): Promise<void> {
    const { getSessionToken } = await import('@descope/react-sdk');
    const token = await getSessionToken();
    const url = `${API_ENDPOINTS.BASE}${API_ENDPOINTS.HTS_REASONING.REASONING_SSE(jobId)}`;

    this.controller = new AbortController();
    console.log('[ReasoningService] Connecting to:', url);

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: this.controller.signal,
      });

      console.log('[ReasoningService] Connection status:', response.status);
      if (!response.ok || !response.body) {
        throw new Error(`Reasoning SSE failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data:')) {
            try {
              const event: ReasoningEvent = JSON.parse(line.slice(5).trim());
              console.log('[ReasoningService] Event received:', event.type, event.phase);
              onEvent(event);
              if (event.type === 'stream_end' || event.type === 'classification_complete') {
                return;
              }
            } catch {
              // Ignore unparseable lines
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.warn('[ReasoningService] SSE error:', err.message);
    }
  }

  /**
   * Get pending ask_user questions for a job.
   */
  async getQuestions(jobId: string): Promise<QuestionsResponse> {
    const response = await apiClient.get(
      API_ENDPOINTS.HTS_REASONING.QUESTIONS(jobId)
    );
    return response.data;
  }

  /**
   * Submit user answers to resume the agent.
   */
  async submitAnswers(jobId: string, answers: Array<{ question: string; answer: string }>): Promise<void> {
    await apiClient.post(
      API_ENDPOINTS.HTS_REASONING.ANSWERS(jobId),
      { answers }
    );
  }

  /**
   * Disconnect from the SSE stream.
   */
  disconnect(): void {
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }
}

const reasoningService = new ReasoningService();
export default reasoningService;
